import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  alpha: number;
  decay: number;
  gravity: number;
  spin?: number;
  angle?: number;
}

const COLORS = [
  "#f43f5e", // Rose / Red
  "#34d399", // Emerald / Green
  "#38bdf8", // Sky / Blue
  "#c084fc", // Purple
  "#facc15", // Gold / Yellow
  "#ec4899", // Pink
  "#fb923c", // Orange
];

export function FireworksCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let particles: Particle[] = [];
    const startTime = Date.now();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Spawn a firework burst at (x, y)
    const createBurst = (x: number, y: number) => {
      const count = 45 + Math.floor(Math.random() * 25);
      const color = COLORS[Math.floor(Math.random() * COLORS.length)]!;
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 6;
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: Math.random() > 0.3 ? color : COLORS[Math.floor(Math.random() * COLORS.length)]!,
          size: 3 + Math.random() * 4,
          alpha: 1,
          decay: 0.015 + Math.random() * 0.015,
          gravity: 0.12,
        });
      }
    };

    // Spawn confetti falling from top
    const createConfettiWave = () => {
      for (let i = 0; i < 35; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: -20 - Math.random() * 50,
          vx: (Math.random() - 0.5) * 3,
          vy: 2 + Math.random() * 4,
          color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
          size: 4 + Math.random() * 6,
          alpha: 1,
          decay: 0.008 + Math.random() * 0.008,
          gravity: 0.05,
          spin: (Math.random() - 0.5) * 0.2,
          angle: Math.random() * Math.PI * 2,
        });
      }
    };

    // Initial bursts
    createBurst(canvas.width * 0.3, canvas.height * 0.35);
    createBurst(canvas.width * 0.7, canvas.height * 0.35);
    createConfettiWave();

    let lastBurstTime = 0;

    const loop = () => {
      const elapsed = Date.now() - startTime;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Periodically trigger new bursts for 4.5 seconds
      if (elapsed < 4500 && elapsed - lastBurstTime > 400 + Math.random() * 300) {
        lastBurstTime = elapsed;
        const rx = canvas.width * (0.2 + Math.random() * 0.6);
        const ry = canvas.height * (0.2 + Math.random() * 0.4);
        createBurst(rx, ry);
      }

      // Update and draw particles
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.alpha -= p.decay;

        if (p.angle !== undefined && p.spin !== undefined) {
          p.angle += p.spin;
        }
      });

      // Filter out dead particles
      particles = particles.filter(p => p.alpha > 0);

      // Draw active particles
      particles.forEach(p => {
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.fillStyle = p.color;

        if (p.angle !== undefined) {
          ctx.translate(p.x, p.y);
          ctx.rotate(p.angle);
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 1.6);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      });

      if (elapsed < 6000 || particles.length > 0) {
        animId = requestAnimationFrame(loop);
      }
    };

    animId = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("resize", resize);
      if (animId) cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fireworks-canvas"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 99999,
      }}
    />
  );
}
